import { useEffect, useRef } from 'preact/hooks';
import * as THREE from 'three';
import { previewRenderer, type PreviewHandle, type PreviewScene } from './previewRenderer';
import { Spin } from './spin';
import { addStudioLights, makeShadowBlob } from './stage';
import { weaponView } from './weaponModel';
import '../../styles/three-ui.css';

/** 3D turntable preview of a weapon model (optionally with a camo). */
export interface WeaponPreviewProps {
  weaponId: string;
  camo?: string;
  /** Fitted scope id (optional): the preview shows it mounted on the weapon. */
  scope?: string;
  className?: string;
  /** Turntable spin (default true). */
  rotate?: boolean;
  /** Allow drag-to-rotate (default true). */
  interactive?: boolean;
  /** Contact shadow under the weapon (default true). */
  shadow?: boolean;
}

/** Barrel pointing right, seen from slightly above. */
const START_YAW = -Math.PI / 2;
const ELEVATION = 0.2;
const FOV = 26;

class WeaponStage implements PreviewScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV, 1, 0.02, 30);
  readonly spin = new Spin(START_YAW + 0.35);
  private readonly turn = new THREE.Group();
  private readonly model = new THREE.Group();
  private readonly shadow: THREE.Mesh;
  private key = '';
  private aspect = 1;
  /** Model bounds centered on the spin axis (meters). */
  private readonly box = new THREE.Box3(new THREE.Vector3(-0.3, -0.1, -0.3), new THREE.Vector3(0.3, 0.1, 0.3));
  private halfHeight = 0.1;
  private t = Math.random() * 10;

  constructor() {
    addStudioLights(this.scene, 1.05);
    this.shadow = makeShadowBlob(1, 1, 0.75);
    this.scene.add(this.turn, this.shadow);
    this.turn.add(this.model);
  }

  setShadow(on: boolean): void {
    this.shadow.visible = on;
  }

  setWeapon(id: string, camo: string | undefined, scope?: string): void {
    const key = `${id}|${camo ?? 'none'}|${scope ?? 'none'}`;
    if (key === this.key) return;
    this.key = key;
    // Geometry is the game's shared weapon cache (per weapon + camo): cleared, never disposed.
    this.model.clear();
    const view = weaponView(id, camo, scope);
    if (!view) return;
    this.model.add(view.object);
    // Spin around the model's center.
    const b = view.bounds;
    const c = b.getCenter(new THREE.Vector3());
    this.model.position.set(-c.x, -c.y, -c.z);
    this.box.copy(b).translate(c.negate());
    this.halfHeight = Math.max(0.03, (b.max.y - b.min.y) / 2);
    this.frame();
  }

  tick(dt: number): boolean {
    this.t += dt;
    this.spin.tick(dt);
    this.turn.rotation.y = this.spin.yaw;
    this.turn.position.y = Math.sin(this.t * 1.6) * this.halfHeight * 0.06;
    return true;
  }

  resize(w: number, h: number): void {
    this.aspect = w / Math.max(1, h);
    this.frame();
  }

  /**
   * Frames the spinning model: finds the smallest camera distance at which the bounding box
   * corners stay inside the view at every turntable angle.
   */
  private frame(): void {
    const cam = this.camera;
    cam.aspect = this.aspect;
    const tv = Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    const th = tv * this.aspect;
    const b = this.box;
    const ce = Math.cos(ELEVATION);
    const se = Math.sin(ELEVATION);
    // Corners in camera-aligned coordinates for 32 spin angles: (lateral, up, depth toward camera).
    const pts: number[] = [];
    for (let k = 0; k < 32; k++) {
      const a = (k / 32) * Math.PI * 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      for (let i = 0; i < 8; i++) {
        const x = i & 1 ? b.max.x : b.min.x;
        const y = i & 2 ? b.max.y : b.min.y;
        const z = i & 4 ? b.max.z : b.min.z;
        const rx = x * ca + z * sa;
        const rz = -x * sa + z * ca;
        pts.push(rx, y * ce - rz * se, y * se + rz * ce);
      }
    }
    const margin = 0.9;
    const fits = (d: number): boolean => {
      for (let i = 0; i < pts.length; i += 3) {
        const depth = d - pts[i + 2];
        if (depth <= 0.01) return false;
        if (Math.abs(pts[i]) / (depth * th) > margin || Math.abs(pts[i + 1]) / (depth * tv) > margin) return false;
      }
      return true;
    };
    let lo = 0.05;
    let hi = 20;
    for (let it = 0; it < 24; it++) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) hi = mid;
      else lo = mid;
    }
    cam.position.set(0, se * hi, ce * hi);
    cam.lookAt(0, 0, 0);
    cam.updateProjectionMatrix();
    const r = Math.max(b.max.x - b.min.x, b.max.z - b.min.z) / 2;
    this.shadow.position.y = -this.halfHeight - 0.03;
    this.shadow.scale.set(r * 2.2, 1, r * 2.2);
  }

  dispose(): void {
    this.model.clear();
  }
}

export function WeaponPreview(props: WeaponPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<WeaponStage | null>(null);
  const handleRef = useRef<PreviewHandle | null>(null);

  if (!stageRef.current) stageRef.current = new WeaponStage();
  const stage = stageRef.current;

  useEffect(() => {
    const handle = previewRenderer.attach(canvasRef.current!, stage);
    handleRef.current = handle;
    return () => {
      handle.detach();
      handleRef.current = null;
      stage.dispose();
      stageRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (props.interactive === false) return;
    return stage.spin.attach(canvasRef.current!, (on) => handleRef.current?.setInteracting(on));
  }, [props.interactive]);

  useEffect(() => {
    stage.setWeapon(props.weaponId, props.camo, props.scope);
    handleRef.current?.invalidate();
  }, [props.weaponId, props.camo, props.scope]);

  useEffect(() => {
    stage.spin.auto = props.rotate === false ? 0 : 0.6;
    stage.setShadow(props.shadow !== false);
    handleRef.current?.invalidate();
  }, [props.rotate, props.shadow]);

  return (
    <div class={`weapon-preview three-preview ${props.className ?? ''}`}>
      <canvas ref={canvasRef} class={`three-canvas${props.interactive === false ? '' : ' spinnable'}`} />
    </div>
  );
}
